import type { ICellRendererComp } from '@ag-grid-community/core';
import type { ReactContainer } from 'src/ReactContainer';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'slugCellRenderers' });

/**
 * Parameters passed to the slug cell renderer factory.
 */
export interface SlugCellRendererParams {
    /** The IoC container for resolving services */
    container: ReactContainer;
    /**
     * Map of slug to field ID for all resolved columns.
     * Allows the renderer to access any column's field ID by its slug.
     */
    resolvedFieldIds: Record<string, string>;
}

/**
 * Definition for a slug-based cell renderer override.
 * These override the default cell renderer for columns matching a specific slug.
 */
export interface SlugCellRendererDefinition {
    /** The slug to match (e.g., 'order::side') */
    slug: string;
    /**
     * Optional list of additional slugs this renderer requires resolved to field IDs.
     * These will be included in resolvedFieldIds if available.
     */
    requiredSlugs?: string[];
    /** Factory function to create the cell renderer */
    createCellRenderer: (params: SlugCellRendererParams) => { new (): ICellRendererComp };
}

/**
 * Registry mapping slug to SlugCellRendererDefinition.
 * Slug cell renderers register themselves here to override default column rendering.
 */
const slugCellRendererRegistry = new Map<string, SlugCellRendererDefinition>();

/**
 * Register a slug cell renderer definition in the registry.
 * This should be called during application initialization.
 */
export function registerSlugCellRenderer(definition: SlugCellRendererDefinition): void {
    if (slugCellRendererRegistry.has(definition.slug)) {
        log.warn(`Slug cell renderer for '${definition.slug}' is already registered. Overwriting.`);
    }
    slugCellRendererRegistry.set(definition.slug, definition);
}

/**
 * Get a slug cell renderer definition by its slug.
 * @returns The definition, or undefined if not found
 */
export function getSlugCellRendererDefinition(slug: string): SlugCellRendererDefinition | undefined {
    return slugCellRendererRegistry.get(slug);
}

/**
 * Get all registered slug cell renderers.
 */
export function getAllSlugCellRenderers(): SlugCellRendererDefinition[] {
    return Array.from(slugCellRendererRegistry.values());
}
